'use client';

import React, { useCallback, useEffect, useMemo } from 'react';

import { Section } from '../../components/common/section/Section';
import {
  cardHubLabel,
  cardLocationText,
  defaultAtHubText
} from '../../components/features/activity/activityCard/cardLocation';
import type { ActivityAvailability } from '../../components/features/activity/unavailableBadge';
import { ListingCard, ListingCardSkeleton } from '../../components/features/search/listingCard/ListingCard';
import NothingFound from '../../components/icons/NothingFound';
import { formatStartingPrice } from '../../lib/utils';

// ---- Memoized card wrapper ----
// All card visual props are passed flat (not nested in an object) so that
// React.memo's shallow comparison works correctly on primitives.
// Callbacks are bound once via useCallback with a stable activityId.

interface MemoizedListingCardItemProps {
  activityId: string;
  // Visual props (flat primitives - shallow comparison works)
  title: string;
  instructor: string;
  blurInstructor?: boolean;
  hideInstructor?: boolean;
  professionalPrefix?: string;
  location: string;
  bookings: string;
  price: string;
  pricePrefix?: string;
  imageUrl?: string;
  thumbnailUrl?: string;
  isHovered: boolean;
  isSelected: boolean;
  isAtHome?: boolean;
  isOnline?: boolean;
  locationKinds?: ('venue' | 'at_home' | 'online')[];
  locationKindLabels?: { venue: string; at_home: string; online: string };
  /** Overrides the venue chip label (e.g. "At <Hub>"); a string so shallow comparison holds. */
  venueKindLabel?: string;
  atHomeLabel?: string;
  onlineLabel?: string;
  viewActivityButtonText?: string;
  availableSlotsButtonText?: string;
  isBookingPage?: boolean;
  date?: string;
  time?: string;
  timeAgo?: string;
  isUpcoming?: boolean;
  isOngoing?: boolean;
  badge?: React.ReactNode;
  onPayNow?: () => void;
  payNowText?: string;
  ratingAverage?: number;
  ratingTotal?: number;
  priceLabel?: string;
  actionMenu?: React.ReactNode;
  unavailableText?: string;
  availability?: ActivityAvailability;
  contextLabel?: string;
  href?: string;
  // Callback props (stable references from parent)
  onActivityClick?: (id: string) => void;
  onActivityHover?: (id: string) => void;
  onActivityLeaveHover?: () => void;
  onViewActivity?: (id: string) => void;
  onViewSlots?: (id: string) => void;
  registerCardRef?: (id: string, element: HTMLDivElement | null) => void;
}

const MemoizedListingCardItem = React.memo<MemoizedListingCardItemProps>(
  ({
    activityId,
    title,
    instructor,
    blurInstructor,
    hideInstructor,
    professionalPrefix,
    location,
    bookings,
    price,
    pricePrefix,
    imageUrl,
    thumbnailUrl,
    isHovered,
    isSelected,
    isAtHome,
    isOnline,
    locationKinds,
    locationKindLabels,
    venueKindLabel,
    atHomeLabel,
    onlineLabel,
    viewActivityButtonText,
    availableSlotsButtonText,
    isBookingPage,
    date,
    time,
    timeAgo,
    isUpcoming,
    isOngoing,
    badge,
    onPayNow,
    payNowText,
    ratingAverage,
    ratingTotal,
    priceLabel,
    actionMenu,
    unavailableText,
    availability,
    contextLabel,
    href,
    onActivityClick,
    onActivityHover,
    onActivityLeaveHover,
    onViewActivity,
    onViewSlots,
    registerCardRef
  }) => {
    const handleClick = useCallback(() => onActivityClick?.(activityId), [activityId, onActivityClick]);
    const handleHover = useCallback(() => onActivityHover?.(activityId), [activityId, onActivityHover]);
    const handleViewActivity = useCallback(() => onViewActivity?.(activityId), [activityId, onViewActivity]);
    const handleViewSlots = useCallback(() => onViewSlots?.(activityId), [activityId, onViewSlots]);
    const handleRef = useCallback(
      (el: HTMLDivElement | null) => registerCardRef?.(activityId, el),
      [activityId, registerCardRef]
    );
    const kindLabels = useMemo(
      () =>
        venueKindLabel && locationKindLabels ? { ...locationKindLabels, venue: venueKindLabel } : locationKindLabels,
      [locationKindLabels, venueKindLabel]
    );

    return (
      <div ref={handleRef}>
        <ListingCard
          title={title}
          instructor={instructor}
          blurInstructor={blurInstructor}
          hideInstructor={hideInstructor}
          professionalPrefix={professionalPrefix}
          location={location}
          bookings={bookings}
          price={price}
          pricePrefix={pricePrefix}
          imageUrl={imageUrl}
          thumbnailUrl={thumbnailUrl}
          isHovered={isHovered}
          isSelected={isSelected}
          isAtHome={isAtHome}
          isOnline={isOnline}
          locationKinds={locationKinds}
          locationKindLabels={kindLabels}
          atHomeLabel={atHomeLabel}
          onlineLabel={onlineLabel}
          viewActivityButtonText={viewActivityButtonText}
          availableSlotsButtonText={availableSlotsButtonText}
          isBookingPage={isBookingPage}
          date={date}
          time={time}
          timeAgo={timeAgo}
          isUpcoming={isUpcoming}
          isOngoing={isOngoing}
          badge={badge}
          onPayNow={onPayNow}
          payNowText={payNowText}
          ratingAverage={ratingAverage}
          ratingTotal={ratingTotal}
          priceLabel={priceLabel}
          actionMenu={actionMenu}
          unavailableText={unavailableText}
          availability={availability}
          contextLabel={contextLabel}
          href={href}
          onClick={handleClick}
          onHover={handleHover}
          onLeaveHover={onActivityLeaveHover}
          onViewActivity={onViewActivity ? handleViewActivity : undefined}
          onViewSlots={onViewSlots ? handleViewSlots : undefined}
        />
      </div>
    );
  }
);

MemoizedListingCardItem.displayName = 'MemoizedListingCardItem';

/**
 * ListingCardsScreen Component
 *
 * A section that displays listing cards in a grid with a status message for found activities.
 */

/** One activity card, in the results or in a section below them (not bookable yet, other areas, popular). */
export interface ListingCardsScreenActivity {
  id: string;
  imageUrl: string;
  /** Square frame for the collapsed thumbnail; defaults to imageUrl. */
  thumbnailUrl?: string;
  title: string;
  professionalName: string;
  blurProfessionalName?: boolean;
  isAtHome?: boolean;
  isOnline?: boolean;
  /** Real delivery-mode mix; 2+ kinds render as compact chips on the card. */
  locationKinds?: ('venue' | 'at_home' | 'online')[];
  bookingsSinceLastYear: number;
  /** `null`/`undefined` → no priced booking yet; price block is hidden. */
  startingPrice?: number | null;
  city?: string;
  /** The Hub the activity runs at: the location line and the venue chip read `atHubText`. */
  hub?: { name: string } | null;
  ratingAverage?: number;
  ratingTotal?: number;
  /** 'none' = approved but not bookable yet (no price, no slots action; badged when the screen has badge text). */
  availability?: ActivityAvailability;
  /** Short muted context after the instructor, e.g. why a suggestion is shown. */
  contextLabel?: string;
}

/** A results card, which may also carry booking context (bookings lists reuse this screen). */
export interface ListingCardsScreenResultActivity extends ListingCardsScreenActivity {
  date?: string;
  time?: string;
  location?: string;
  status?: 'upcoming' | 'confirmed' | 'completed' | 'cancelled' | 'pending';
  currency?: string;
  timeAgo?: string;
  isUpcoming?: boolean;
  isOngoing?: boolean;
  /** Optional badge element (e.g., payment status badge) */
  badge?: React.ReactNode;
  /** Handler for Pay Now action (when payment is pending) */
  onPayNow?: () => void;
  /** Text for Pay Now button */
  payNowText?: string;
  /** Optional action menu (e.g. 3-dot dropdown) rendered on the card */
  actionMenu?: React.ReactNode;
}

export interface ListingCardsScreenProps {
  /**
   * Whether to show the activity count text
   * @default true
   */
  showActivityCount?: boolean;

  /**
   * Whether to hide the instructor name on activity cards
   * @default false
   */
  hideInstructor?: boolean;

  /**
   * Array of activity objects to display
   */
  activities: ListingCardsScreenResultActivity[];

  /**
   * Handler for viewing full activity details
   */
  onViewActivity?: (activityId: string) => void;

  /**
   * Handler for viewing available slots
   */
  onViewSlots?: (activityId: string) => void;

  /**
   * Callback to register a ref for an activity card
   */
  registerCardRef?: (id: string, element: HTMLDivElement | null) => void;

  /**
   * Total count of activities (may be different from activities.length if paginated)
   */
  totalCount: number;

  /**
   * Handler for when an activity card is clicked
   */
  onActivityClick?: (activityId: string) => void;

  /**
   * Builds the activity page URL for a card. When provided, each (non-booking)
   * card exposes a real crawlable link; plain clicks still go through
   * `onActivityClick` / `onViewActivity`.
   */
  getActivityHref?: (activityId: string) => string;

  /**
   * Handler for when an activity card is hovered
   */
  onActivityHover?: (activityId: string) => void;

  /**
   * Handler for when hover leaves an activity card
   */
  onActivityLeaveHover?: () => void;

  /**
   * ID of the currently hovered activity to highlight it
   */
  hoveredActivityId?: string;

  /**
   * ID of the currently selected activity
   */
  selectedActivityId?: string;

  /**
   * Whether the activities are currently loading
   */
  isLoading?: boolean;

  /**
   * Number of skeleton cards to show when loading
   * @default 3
   */
  skeletonCount?: number;

  /**
   * Whether there was an error loading the activities
   */
  isError?: boolean;

  /**
   * Translated prefix for single activity found text
   * @default ""
   */
  activitiesFoundSingularPrefix?: string;

  /**
   * Translated suffix for single activity found text
   * @default " Activity found"
   */
  activitiesFoundSingularSuffix?: string;

  /**
   * Translated prefix for multiple activities found text
   * @default ""
   */
  activitiesFoundPluralPrefix?: string;

  /**
   * Translated suffix for multiple activities found text
   * @default " Activities found"
   */
  activitiesFoundPluralSuffix?: string;

  /**
   * Translated title for no activities found message
   * @default "No activities found"
   */
  noActivitiesFoundTitle?: string;

  /**
   * Translated text for no activities found message
   * @default "No activities found. Try a different category or search term."
   */
  noActivitiesFoundText?: string;

  /**
   * Optional action rendered under the no-activities message - e.g. a button
   * that widens the search to where matches were found.
   */
  emptyStateAction?: React.ReactNode;

  /**
   * Translated text for loading message
   * @default "Loading activities..."
   */
  loadingText?: string;

  /**
   * Translated text shown when the list fails to load
   * @default "Error loading activities. Please try again later."
   */
  errorText?: string;

  /**
   * Translated text for "At Home" location
   * @default "At Home"
   */
  atHomeText?: string;

  /**
   * Translated text for "At Venue" location
   * @default "At Venue"
   */
  atVenueText?: string;

  /**
   * Translated venue of an activity at a Hub
   * @default (hubName) => `At ${hubName}`
   */
  atHubText?: (hubName: string) => string;

  /**
   * Translated text for an online (visio) booking
   * @default "Online"
   */
  onlineText?: string;

  /**
   * Translated prefix for bookings count text
   * @default ""
   */
  bookingsInYearPrefix?: string;

  /**
   * Translated middle text between count and year for bookings text
   * @default " Bookings in "
   */
  bookingsInYearMiddle?: string;

  /**
   * Translated suffix for bookings count text
   * @default ""
   */
  bookingsInYearSuffix?: string;

  /**
   * Translated text for price suffix ("onwards")
   * @default "onwards"
   */
  onwardsText?: string;

  /**
   * Translated label shown above the price on mobile booking cards.
   * @default "Price"
   */
  priceLabel?: string;

  /**
   * Translated prefix for professional name (e.g. "Par", "By")
   * @default "By"
   */
  professionalPrefixText?: string;

  /**
   * Translated text for "View Activity" button
   * @default "View Activity"
   */
  viewActivityButtonText?: string;

  /**
   * Translated text for "Available Slots" button
   * @default "Available Slots"
   */
  availableSlotsButtonText?: string;

  /**
   * Whether this screen is displayed on a booking page
   * If true, cards will not expand even when selected
   * @default false
   */
  isBookingPage?: boolean;

  /**
   * Handler for loading more items (infinite scroll)
   */
  onLoadMore?: () => void;

  /**
   * Whether there are more pages to load
   * @default false
   */
  hasNextPage?: boolean;

  /**
   * Whether currently loading more items
   * @default false
   */
  isLoadingMore?: boolean;

  /**
   * Text to show when loading more
   * @default "Loading more..."
   */
  loadingMoreText?: string;

  /** Suggestions listed in their own section below the results (or under the empty state); the API never repeats a result in them. */
  suggestions?: ListingCardsScreenActivity[];

  /**
   * Badge text for result activities that are not bookable yet. Without it they
   * render like any other card (no price, no "available slots" action).
   */
  unavailableText?: string;

  /**
   * Title of the suggestions section
   * @default "Other suggestions"
   */
  suggestionsTitle?: string;

  /**
   * Locale for currency formatting (e.g. 'fr-FR', 'en-US')
   * @default 'fr-FR'
   */
  locale?: string;
}

/**
 * ListingCardsScreen component for displaying a grid of listing cards with status message
 *
 * @example
 * ```tsx
 * <ListingCardsScreen
 *   activities={activities}
 *   totalCount={totalCount}
 *   onActivityClick={(id) => console.log(`Activity ${id} clicked`)}
 * />
 * ```
 */
export const ListingCardsScreen: React.FC<ListingCardsScreenProps> = ({
  activities,
  totalCount,
  onActivityClick,
  getActivityHref,
  isLoading = false,
  skeletonCount = 3,
  isError = false,
  showActivityCount = true,
  hideInstructor = false,
  activitiesFoundSingularPrefix = '',
  activitiesFoundSingularSuffix = ' Activity found',
  activitiesFoundPluralPrefix = '',
  activitiesFoundPluralSuffix = ' Activities found',
  noActivitiesFoundTitle = 'No activities found',
  noActivitiesFoundText = 'No activities found. Try a different category or search term.',
  emptyStateAction,
  atHomeText = 'At Home',
  atVenueText = 'At Venue',
  atHubText = defaultAtHubText,
  onlineText = 'Online',
  bookingsInYearPrefix = '',
  bookingsInYearMiddle = ' Bookings in ',
  bookingsInYearSuffix = '',
  onwardsText = 'onwards',
  priceLabel = 'Price',
  professionalPrefixText = 'By',
  viewActivityButtonText = 'View Activity',
  availableSlotsButtonText = 'Available Slots',
  isBookingPage = false,
  onActivityHover,
  onActivityLeaveHover,
  hoveredActivityId,
  selectedActivityId,
  registerCardRef,
  onViewActivity,
  onViewSlots,
  onLoadMore,
  hasNextPage = false,
  isLoadingMore = false,
  loadingText = 'Loading activities...',
  loadingMoreText = 'Loading more...',
  errorText = 'Error loading activities. Please try again later.',
  suggestions,
  suggestionsTitle = 'Other suggestions',
  unavailableText,
  locale = 'fr-FR'
}) => {
  const hasActivities = activities.length > 0;

  // Chip labels for the real location-kind mix (venue / at_home / online). The
  // venue chip reuses the generic "at venue" label here (per-card city stays on
  // the single-kind fallback, where there's room for it).
  const locationKindLabels = useMemo(
    () => ({ venue: atVenueText, at_home: atHomeText, online: onlineText }),
    [atVenueText, atHomeText, onlineText]
  );

  // Using boolean expressions with && for TypeScript to recognize usage
  const showLoading = isLoading && true;
  const showError = isError && true;

  // State to track the last card element for intersection observer
  const [lastCardElement, setLastCardElement] = React.useState<HTMLDivElement | null>(null);
  const debounceTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Enhanced registerCardRef that tracks the last card
  const enhancedRegisterCardRef = React.useCallback(
    (id: string, element: HTMLDivElement | null) => {
      // Call the original registerCardRef if provided
      if (registerCardRef) {
        registerCardRef(id, element);
      }

      // Check if this is the last card and set it as our intersection target
      const currentIndex = activities.findIndex((activity) => activity.id === id);
      const isLastCard = currentIndex === activities.length - 1;

      if (isLastCard) {
        setLastCardElement(element);
      }
    },
    [activities, registerCardRef]
  );

  // Optimized intersection observer for the last card with debouncing
  useEffect(() => {
    if (!lastCardElement || !onLoadMore || !hasNextPage || isLoadingMore) return;

    const handleIntersection = ([entry]: IntersectionObserverEntry[]) => {
      if (entry.isIntersecting) {
        // Clear any existing timeout
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        // Debounce the load more call to prevent rapid requests
        debounceTimeoutRef.current = setTimeout(() => {
          onLoadMore();
        }, 300);
      }
    };

    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: '200px', // Start loading when last card is 200px from viewport
      threshold: 0.1
    });

    observer.observe(lastCardElement);

    return () => {
      observer.disconnect();
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [lastCardElement, onLoadMore, hasNextPage, isLoadingMore]);

  // Simple loading indicator component
  const LoadingIndicator = () => {
    if (!isLoadingMore) return null;

    return (
      <div className="flex items-center justify-center gap-3 text-gray-600 py-8">
        <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
        <span className="text-sm">{loadingMoreText}</span>
      </div>
    );
  };

  // One renderer for every card. Screen-level `hideInstructor` / `priceLabel`
  // apply to the results only, not to the sections below them.
  const resultCardProps = { hideInstructor, priceLabel };
  const renderCard = (activity: ListingCardsScreenResultActivity, resultProps?: typeof resultCardProps) => {
    const venueLabel = cardHubLabel(activity.hub, atHubText);
    return (
      <MemoizedListingCardItem
        key={activity.id}
        activityId={activity.id}
        href={getActivityHref?.(activity.id)}
        title={activity.title}
        instructor={activity.professionalName}
        blurInstructor={activity.blurProfessionalName}
        hideInstructor={resultProps?.hideInstructor}
        professionalPrefix={professionalPrefixText}
        location={cardLocationText(activity, {
          online: onlineText,
          atHome: atHomeText,
          atVenue: atVenueText,
          atHub: atHubText
        })}
        bookings={
          activity.bookingsSinceLastYear && activity.bookingsSinceLastYear > 5
            ? `${bookingsInYearPrefix}${activity.bookingsSinceLastYear}${bookingsInYearMiddle}${new Date().getFullYear()}${bookingsInYearSuffix}`
            : ''
        }
        {...(formatStartingPrice(activity.startingPrice, { locale, onwardsText }) ?? {
          price: '',
          pricePrefix: ''
        })}
        imageUrl={activity.imageUrl}
        thumbnailUrl={activity.thumbnailUrl}
        isHovered={hoveredActivityId === activity.id}
        isSelected={selectedActivityId === activity.id}
        isAtHome={activity.isAtHome}
        isOnline={activity.isOnline}
        locationKinds={activity.locationKinds}
        locationKindLabels={locationKindLabels}
        venueKindLabel={venueLabel}
        atHomeLabel={atHomeText}
        onlineLabel={onlineText}
        viewActivityButtonText={viewActivityButtonText}
        availableSlotsButtonText={availableSlotsButtonText}
        isBookingPage={isBookingPage}
        date={activity.date}
        time={activity.time}
        timeAgo={activity.timeAgo}
        isUpcoming={activity.isUpcoming}
        isOngoing={activity.isOngoing}
        badge={activity.badge}
        onPayNow={activity.onPayNow}
        payNowText={activity.payNowText}
        ratingAverage={activity.ratingAverage}
        ratingTotal={activity.ratingTotal}
        priceLabel={resultProps?.priceLabel}
        actionMenu={activity.actionMenu}
        unavailableText={unavailableText}
        availability={activity.availability}
        contextLabel={activity.contextLabel}
        onActivityClick={onActivityClick}
        onActivityHover={onActivityHover}
        onActivityLeaveHover={onActivityLeaveHover}
        onViewActivity={onViewActivity}
        onViewSlots={onViewSlots}
        registerCardRef={enhancedRegisterCardRef}
      />
    );
  };

  // Handle loading state with skeletons only when we still have cards to render.
  // For empty-state refetches we keep the section mounted and show an inline loader block.
  if (showLoading && hasActivities) {
    return (
      <div className="flex flex-col gap-3 w-full">
        <div className="grid grid-cols-1 gap-5 w-full">
          {Array.from({ length: skeletonCount }, (_, index) => (
            <ListingCardSkeleton key={`skeleton-${index}`} />
          ))}
        </div>
      </div>
    );
  }

  // Handle error state
  if (showError) {
    return (
      <Section className="pt-0 pb-5 md:pb-15">
        <div className="text-xs font-semibold text-red-600">{errorText}</div>
      </Section>
    );
  }

  const suggestionsSection = (className: string) =>
    suggestions && suggestions.length > 0 ? (
      <div className={className}>
        <h4 className="text-base font-semibold text-neutral-900 mb-4 text-left">{suggestionsTitle}</h4>
        <div className="grid grid-cols-1 gap-5">{suggestions.map((activity) => renderCard(activity))}</div>
      </div>
    ) : null;

  return (
    <div className="flex flex-col gap-3">
      {/* Results status - shows count or "No activities" message */}
      {showActivityCount && hasActivities && (
        <div className="text-xs font-semibold text-[#1F242F]">
          {totalCount === 1
            ? `${activitiesFoundSingularPrefix}${totalCount}${activitiesFoundSingularSuffix}`
            : `${activitiesFoundPluralPrefix}${totalCount}${activitiesFoundPluralSuffix}`}
        </div>
      )}

      {/* Empty state with NothingFound icon */}
      {!hasActivities && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-full max-w-xl min-h-[240px] flex items-center justify-center mb-8 px-4">
            {showLoading ? (
              <div className="flex items-center justify-center gap-3 text-gray-600">
                <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                <span className="text-sm">{loadingText}</span>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center">
                <NothingFound className="w-32 h-32 mb-6 text-neutral-400" />
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">{noActivitiesFoundTitle}</h3>
                  <p className="text-sm text-neutral-600 max-w-md">{noActivitiesFoundText}</p>
                  {emptyStateAction && <div className="mt-4 flex justify-center">{emptyStateAction}</div>}
                </div>
              </div>
            )}
          </div>

          {suggestionsSection('w-full max-w-4xl')}
        </div>
      )}

      {/* Listing cards grid - only render when there are activities */}
      {hasActivities && (
        <>
          <div className="grid grid-cols-1 gap-5">
            {activities.map((activity) => renderCard(activity, resultCardProps))}
          </div>

          {/* Loading indicator for infinite scroll */}
          <LoadingIndicator />

          {suggestionsSection('mt-8 pt-6 border-t border-neutral-200')}
        </>
      )}
    </div>
  );
};
